package com.anjuros.website.redesign.styles

import com.anjuros.website.redesign.theme.RdColor
import com.anjuros.website.redesign.theme.RdFont
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.DisplayStyle

// Toolbar shown above every template preview: one slim dark bar on all screen sizes.
// Phones (below MD) get icon-only back, an info button that opens a bottom sheet with the
// preview switch, and the floating "Get this website" pill. Wider screens show the switch in the bar.

private const val BAR_HEIGHT = "60px"
private const val CARD_SHADOW = "0 18px 40px -18px rgba(30,40,52,.55)"

val TtBarStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(RdColor.Band)
            .color(RdColor.BandText)
            .css(
                "box-sizing" to "border-box",
                "width" to "100%",
                "height" to BAR_HEIGHT,
                "display" to "flex",
                "align-items" to "center",
                "gap" to "4px",
                "padding" to "0 8px",
                "position" to "relative",
                "font-family" to RdFont.BODY_STACK,
                "box-shadow" to "0 10px 24px -16px rgba(30,40,52,.7)"
            )
    }
    Breakpoint.MD { Modifier.css("gap" to "16px", "padding" to "0 24px") }
}

val TtBackStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.BandText)
            .css(
                "flex" to "none",
                "display" to "inline-flex",
                "align-items" to "center",
                "justify-content" to "center",
                "gap" to "6px",
                "min-width" to "44px",
                "height" to "44px",
                "border-radius" to "10px",
                "text-decoration" to "none",
                "font" to "600 14.5px/1 ${RdFont.BODY_STACK}",
                "transition" to "background .15s"
            )
    }
    hover { Modifier.css("background" to "rgba(255,255,255,.08)") }
    Breakpoint.MD { Modifier.css("height" to "40px", "padding" to "0 12px 0 8px") }
}

val TtBackLabelStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD { Modifier.display(DisplayStyle.Inline) }
}

val TtDividerStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier.css("display" to "block", "flex" to "none", "width" to "1px", "height" to "28px", "background" to "rgba(180,198,220,.3)")
    }
}

val TtTitleWrapStyle = CssStyle.base {
    Modifier.css("flex" to "1", "min-width" to "0", "display" to "flex", "align-items" to "center", "gap" to "4px", "position" to "relative")
}

val TtTitleStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Paper)
            .css(
                "margin" to "0",
                "min-width" to "0",
                "font" to "750 17px/1.15 ${RdFont.DISPLAY_STACK}",
                "font-stretch" to "108%",
                "white-space" to "nowrap",
                "overflow" to "hidden",
                "text-overflow" to "ellipsis"
            )
    }
    Breakpoint.MD { Modifier.css("font-size" to "18px") }
}

val TtIconButtonStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.LogoBlue)
            .css(
                "flex" to "none",
                "width" to "44px",
                "height" to "44px",
                "border" to "0",
                "border-radius" to "10px",
                "background" to "transparent",
                "display" to "grid",
                "place-items" to "center",
                "cursor" to "pointer",
                "padding" to "0",
                "transition" to "background .15s"
            )
    }
    hover { Modifier.css("background" to "rgba(255,255,255,.08)") }
    Breakpoint.MD { Modifier.css("width" to "40px", "height" to "40px") }
}

// ---------- Preview switch ----------

/** The switch inside the bar, from MD up. Phones find it in the bottom sheet. */
val TtModeGroupBarStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier.css(
            "display" to "flex",
            "flex" to "none",
            "gap" to "2px",
            "padding" to "3px",
            "border-radius" to "11px",
            "background" to "rgba(255,255,255,.07)",
            "border" to "1px solid rgba(180,198,220,.22)",
            "position" to "relative"
        )
    }
}

/** Lifts the switch above the page dimmer while the first-visit hint is showing. */
val TtModeGroupSpotlightStyle = CssStyle.base {
    Modifier.css("box-shadow" to "0 0 0 3px rgb(180,198,220), 0 0 32px rgba(180,198,220,.55)")
}

val TtModeButtonBarStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.LogoBlue)
            .css(
                "height" to "34px",
                "padding" to "0 12px",
                "border" to "0",
                "border-radius" to "8px",
                "background" to "transparent",
                "display" to "flex",
                "align-items" to "center",
                "gap" to "7px",
                "font" to "600 14px/1 ${RdFont.BODY_STACK}",
                "white-space" to "nowrap",
                "cursor" to "pointer",
                "transition" to "background .15s, color .15s"
            )
    }
    hover { Modifier.color(RdColor.Paper) }
}

val TtModeButtonBarActiveStyle = CssStyle {
    base { Modifier.backgroundColor(RdColor.Paper).color(RdColor.Ink) }
    hover { Modifier.color(RdColor.Ink) }
}

val TtModeGroupSheetStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.SkySoft)
        .css(
            "display" to "grid",
            "grid-template-columns" to "repeat(2, minmax(0, 1fr))",
            "gap" to "4px",
            "padding" to "4px",
            "border-radius" to "12px"
        )
}

val TtModeButtonSheetStyle = CssStyle.base {
    Modifier
        .color(RdColor.Brand)
        .css(
            "height" to "44px",
            "border" to "0",
            "border-radius" to "9px",
            "background" to "transparent",
            "display" to "flex",
            "align-items" to "center",
            "justify-content" to "center",
            "gap" to "8px",
            "font" to "600 14.5px/1 ${RdFont.BODY_STACK}",
            "cursor" to "pointer"
        )
}

val TtModeButtonSheetActiveStyle = CssStyle.base {
    Modifier.backgroundColor(RdColor.Paper).color(RdColor.Ink).css("box-shadow" to "0 1px 3px rgba(30,40,52,.18)")
}

// ---------- About this design: popover (MD up) and bottom sheet (phones) ----------

val TtPopoverStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.Paper)
        .color(RdColor.Ink)
        .css(
            "display" to "none",
            "position" to "absolute",
            "top" to "calc(100% + 18px)",
            "left" to "0",
            "z-index" to "4",
            "box-sizing" to "border-box",
            "width" to "420px",
            "max-width" to "calc(100vw - 32px)",
            "padding" to "20px 22px 22px",
            "border" to "1px solid rgb(220,227,236)",
            "border-radius" to "14px",
            "box-shadow" to CARD_SHADOW,
            "flex-direction" to "column",
            "gap" to "12px"
        )
}

val TtPopoverOpenStyle = CssStyle {
    Breakpoint.MD { Modifier.display(DisplayStyle.Flex) }
}

val TtCardHeadStyle = CssStyle.base {
    Modifier.css("display" to "flex", "align-items" to "flex-start", "justify-content" to "space-between", "gap" to "12px")
}

val TtEyebrowStyle = CssStyle.base {
    Modifier
        .color(RdColor.Muted)
        .css("font" to "500 11px/1 ${RdFont.MONO_STACK}", "letter-spacing" to ".12em", "text-transform" to "uppercase")
}

val TtCloseButtonStyle = CssStyle {
    base {
        Modifier
            .color(RdColor.Muted)
            .css(
                "flex" to "none",
                "width" to "44px",
                "height" to "44px",
                "margin" to "-12px -12px 0 0",
                "border" to "0",
                "border-radius" to "10px",
                "background" to "transparent",
                "display" to "grid",
                "place-items" to "center",
                "cursor" to "pointer",
                "padding" to "0"
            )
    }
    hover { Modifier.color(RdColor.Ink) }
}

val TtDescriptionStyle = CssStyle.base {
    Modifier.css("margin" to "0", "font" to "400 15.5px/1.55 ${RdFont.BODY_STACK}")
}

val TtCardButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(RdColor.Brand)
            .color(RdColor.BrandInk)
            .css(
                "display" to "inline-flex",
                "align-self" to "flex-start",
                "align-items" to "center",
                "justify-content" to "center",
                "height" to "44px",
                "padding" to "0 18px",
                "border" to "0",
                "border-radius" to "10px",
                "text-decoration" to "none",
                "font" to "600 15px/1 ${RdFont.BODY_STACK}",
                "cursor" to "pointer",
                "transition" to "background .15s"
            )
    }
    hover { Modifier.backgroundColor(RdColor.Band) }
}

val TtSheetScrimStyle = CssStyle.base {
    Modifier.css(
        "display" to "none",
        "position" to "fixed",
        "inset" to "0",
        "z-index" to "5",
        "background" to "rgba(15,20,28,.55)",
        "flex-direction" to "column",
        "justify-content" to "flex-end"
    )
}

val TtSheetScrimOpenStyle = CssStyle {
    base { Modifier.display(DisplayStyle.Flex) }
    Breakpoint.MD { Modifier.display(DisplayStyle.None) }
}

val TtSheetStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.Paper)
        .color(RdColor.Ink)
        .css(
            "border-radius" to "20px 20px 0 0",
            "padding" to "10px 24px calc(32px + env(safe-area-inset-bottom, 0px))",
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "16px",
            "max-height" to "85vh",
            "overflow-y" to "auto",
            "overscroll-behavior" to "contain" // scrolling inside the sheet never moves the page
        )
}

val TtSheetHandleStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.Line)
        .css("width" to "40px", "height" to "4px", "border-radius" to "2px", "align-self" to "center")
}

val TtSheetTitleStyle = CssStyle.base {
    Modifier.css("display" to "block", "margin-top" to "8px", "font" to "800 24px/1.1 ${RdFont.DISPLAY_STACK}", "font-stretch" to "110%")
}

val TtSheetDescriptionStyle = CssStyle.base {
    Modifier.color(RdColor.Muted).css("margin" to "0", "font" to "400 16px/1.55 ${RdFont.BODY_STACK}")
}

val TtStackStyle = CssStyle.base {
    Modifier.css("display" to "flex", "flex-direction" to "column", "gap" to "8px")
}

val TtSheetButtonStyle = CssStyle.base {
    Modifier.css("align-self" to "stretch", "height" to "48px", "font-size" to "16px")
}

// ---------- First-visit hint (MD up) ----------

val TtHintStyle = CssStyle.base {
    Modifier
        .backgroundColor(RdColor.Paper)
        .color(RdColor.Ink)
        .css(
            "position" to "absolute",
            "top" to "calc(100% + 18px)",
            "right" to "0",
            "z-index" to "4",
            "box-sizing" to "border-box",
            "width" to "380px",
            "padding" to "22px",
            "border-radius" to "14px",
            "box-shadow" to CARD_SHADOW,
            "display" to "flex",
            "flex-direction" to "column",
            "gap" to "12px",
            "font" to "400 15px/1.55 ${RdFont.BODY_STACK}"
        )
}

val TtHintTitleStyle = CssStyle.base {
    Modifier.css("margin" to "0", "font" to "750 18px/1.2 ${RdFont.DISPLAY_STACK}", "font-stretch" to "105%")
}

val TtHintButtonStyle = CssStyle.base {
    Modifier.css("align-self" to "flex-end")
}

// ---------- Floating "Get this website" pill ----------

val TtPillStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(RdColor.LogoBlue)
            .color(RdColor.Ink)
            .css(
                "position" to "fixed",
                "left" to "50%",
                "bottom" to "calc(20px + env(safe-area-inset-bottom, 0px))",
                "transform" to "translateX(-50%)",
                "z-index" to "40", // under the page dimmer (50) and the template menus
                "box-sizing" to "border-box",
                "max-width" to "calc(100vw - 32px)",
                "height" to "50px",
                "padding" to "0 22px",
                "border-radius" to "999px",
                "border" to "1px solid rgba(255,255,255,.5)",
                "box-shadow" to "0 16px 36px -12px rgba(0,0,0,.6)",
                "display" to "flex",
                "align-items" to "center",
                "gap" to "8px",
                "white-space" to "nowrap",
                "text-decoration" to "none",
                "font" to "600 15px/1 ${RdFont.BODY_STACK}",
                "transition" to "background .15s, transform .15s"
            )
    }
    hover { Modifier.backgroundColor(RdColor.Paper).css("transform" to "translate(-50%, -2px)") }
    Breakpoint.MD { Modifier.css("height" to "52px", "padding" to "0 26px", "bottom" to "28px", "font-size" to "16px") }
}

val TtPillLabelStyle = CssStyle.base {
    Modifier.css("overflow" to "hidden", "text-overflow" to "ellipsis", "line-height" to "1.35")
}

// ---------- Links back into the site ----------

/** Text wordmark linking home (the logo image's dark grey line would vanish on the dark bar). Wider screens only. */
val TtLogoStyle = CssStyle {
    base { Modifier.display(DisplayStyle.None) }
    Breakpoint.MD {
        Modifier.css(
            "display" to "flex",
            "flex" to "none",
            "flex-direction" to "column",
            "justify-content" to "center",
            "gap" to "2px",
            "height" to "44px",
            "padding" to "0 4px",
            "text-decoration" to "none"
        )
    }
}

val TtLogoTopStyle = CssStyle.base {
    Modifier.color(RdColor.LogoBlue)
        .css("font" to "800 15px/1 ${RdFont.DISPLAY_STACK}", "font-stretch" to "120%", "letter-spacing" to ".04em")
}

val TtLogoBottomStyle = CssStyle.base {
    Modifier.color(RdColor.BandText)
        .css("font" to "500 10.5px/1 ${RdFont.DISPLAY_STACK}", "font-stretch" to "120%", "letter-spacing" to ".06em")
}

/** "More: Home · Designs · Pricing" under the button in the About box. */
val TtMoreLinksStyle = CssStyle {
    base {
        Modifier.css(
            "display" to "flex",
            "align-items" to "center",
            "flex-wrap" to "wrap",
            "gap" to "4px 14px",
            "padding-top" to "12px",
            "border-top" to "1px solid rgb(220,227,236)",
            "font" to "600 14px/1.4 ${RdFont.BODY_STACK}"
        )
    }
    cssRule(" a") { Modifier.color(RdColor.Brand).css("text-decoration" to "none") }
    cssRule(" a:hover") { Modifier.css("text-decoration" to "underline") }
    // A thin vertical divider between links: Home | Designs | Pricing.
    cssRule(" a + a") { Modifier.css("border-left" to "1px solid rgb(220,227,236)", "padding-left" to "14px") }
}

/** The same links in the phone sheet, as three full-size buttons. */
val TtSheetLinksStyle = CssStyle {
    base {
        Modifier.css(
            "display" to "grid",
            "grid-template-columns" to "repeat(3, minmax(0, 1fr))",
            "gap" to "8px",
            "padding-top" to "14px",
            "border-top" to "1px solid rgb(220,227,236)"
        )
    }
    cssRule(" a") {
        Modifier.color(RdColor.Brand).css(
            "display" to "grid",
            "place-items" to "center",
            "height" to "44px",
            "border" to "1px solid rgb(220,227,236)",
            "border-radius" to "10px",
            "text-decoration" to "none",
            "font" to "600 14.5px/1 ${RdFont.BODY_STACK}"
        )
    }
}
